/**
 * useFocusTrap カスタム hook
 *
 * dialog やドロワー内でフォーカスをトラップし、
 * Tab / Shift+Tab でフォーカスが外に出るのを防ぐ。
 * Escape キーで閉じる処理も担う。
 */

import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

interface UseFocusTrapOptions {
  /** トラップを有効にするかどうか */
  isActive: boolean;

  /** Escape キー押下時のコールバック */
  onEscape: () => void;
}

/**
 * フォーカストラップ hook
 *
 * 使用例:
 * ```tsx
 * const drawerRef = useFocusTrap({ isActive: isOpen, onEscape: onClose });
 * return <div ref={drawerRef}>...</div>;
 * ```
 */
export function useFocusTrap<T extends HTMLElement = HTMLDivElement>({
  isActive,
  onEscape,
}: UseFocusTrapOptions) {
  const containerRef = useRef<T>(null);

  // Escape キーでパネルを閉じる
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onEscape();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isActive, onEscape]);

  // Tab キーでフォーカスをコンテナ内に閉じ込める
  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;
    const focusable =
      container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };

    // ドロワーが開いたら最初の要素にフォーカス
    first?.focus();

    document.addEventListener('keydown', handleTabKey);
    return () => {
      document.removeEventListener('keydown', handleTabKey);
    };
  }, [isActive]);

  return containerRef;
}
